import React, { useState } from "react";
import { DatePicker, Form, Input, InputNumber, Select, Spin, message } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import EngagementService from "../../../ClientView/services/EngagementService";
import { usePartnershipContext } from "../../../../core/context/PartnershipContext";
import styles from "./CreateEngagement.module.css";

// ── CreateEngagement ──────────────────────────────────────────────────────────

export const CreateEngagement: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const { selectedClient } = usePartnershipContext();

  const handleSave = async () => {
    let values: Record<string, unknown>;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      await EngagementService.createEngagement(
        {
          engagementType: values.engagementType as string,
          description: values.description as string,
          taxYear: values.taxYear as number,
        },
        selectedClient?.id,
      );
      message.success("Engagement created");
      navigate("/engagements");
    } catch {
      message.error("Failed to create engagement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.pageWrap}>
      {/* ── Back link ── */}
      <button className={styles.backLink} onClick={() => navigate("/engagements")}>
        <LeftOutlined style={{ marginRight: 6, fontSize: 12 }} />
        Back to Engagements
      </button>

      {/* ── Page heading ── */}
      <h1 className={styles.pageTitle}>Create Engagement</h1>
      <p className={styles.pageSub}>
        Fill in the details below to initiate a new engagement.
      </p>

      {/* ── Form body ── */}
      <div className={styles.formOuter}>
        <Form form={form} layout="vertical" initialValues={{ taxYear: new Date().getFullYear() }}>
          <div className={styles.formGrid}>

            {/* ── LEFT — primary info (backend-backed fields) ── */}
            <div className={styles.card}>
              <div className={styles.cardHead}>
                <h2 className={styles.cardTitle}>Primary information</h2>
                <div className={styles.cardMeta}>Required to save this engagement</div>
              </div>

              <Form.Item
                name="description"
                label="Engagement name"
                rules={[{ required: true, message: "Please enter an engagement name" }]}
              >
                {/* EngagementDto.description is the only text identifier for an engagement */}
                <Input
                  className={styles.antInput}
                  placeholder="e.g. FY24 Annual Audit"
                />
              </Form.Item>

              <Form.Item
                name="engagementType"
                label="Engagement type"
                rules={[{ required: true, message: "Please select a type" }]}
              >
                <Select className={styles.antSelect} placeholder="Select type">
                  <Select.Option value="Audit">Audit</Select.Option>
                  <Select.Option value="Tax">Tax</Select.Option>
                  <Select.Option value="Compliance">Compliance</Select.Option>
                  <Select.Option value="Consulting">Consulting</Select.Option>
                  <Select.Option value="Advisory">Advisory</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="taxYear"
                label="Tax year"
                rules={[{ required: true, message: "Please enter a tax year" }]}
              >
                <InputNumber
                  min={2000}
                  max={2100}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div>

            {/* ── RIGHT — additional fields ── */}
            <div className={styles.rightCol}>

              <div className={styles.card}>
                <div className={styles.cardHead}>
                  <h2 className={styles.cardTitle}>Status &amp; Duration</h2>
                </div>
                <Form.Item name="status" label="Status" initialValue="Active">
                  <Select className={styles.antSelect}>
                    <Select.Option value="Active">Active</Select.Option>
                    <Select.Option value="In Progress">In Progress</Select.Option>
                    <Select.Option value="Completed">Completed</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name="duration" label="Duration" initialValue="Single-year">
                  <Select className={styles.antSelect}>
                    <Select.Option value="Single-year">Single-year</Select.Option>
                    <Select.Option value="Multi-year">Multi-year</Select.Option>
                  </Select>
                </Form.Item>
              </div>

              <div className={styles.card}>
                <h2 className={styles.cardTitle}>Timeline</h2>
                <Form.Item
                  name="startDate"
                  label="Start Date"
                  rules={[{ required: true, message: "Start date is required" }]}
                >
                  <DatePicker style={{ width: "100%" }} format="MM/DD/YYYY" />
                </Form.Item>
                <Form.Item name="endDate" label="End Date">
                  <DatePicker style={{ width: "100%" }} format="MM/DD/YYYY" />
                </Form.Item>
              </div>

              <div className={styles.card}>
                <h2 className={styles.cardTitle}>Lead Partner</h2>
                <Form.Item name="leadPartner" label="Lead Partner Name">
                  <Input className={styles.antInput} placeholder="e.g. John Anderson" />
                </Form.Item>
              </div>

              <div className={styles.card}>
                <h2 className={styles.cardTitle}>Notes</h2>
                <Form.Item name="notes" label="Notes">
                  <Input.TextArea
                    className={styles.antInput}
                    rows={3}
                    placeholder="Additional context for this engagement..."
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </Form>
      </div>

      {/* ── Footer bar ── */}
      <div className={styles.footBar}>
        <div />
        <div className={styles.footActions}>
          <button
            className={styles.btnOutline}
            onClick={() => navigate("/engagements")}
          >
            Cancel
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleSave}
            disabled={submitting}
          >
            {submitting ? <Spin size="small" style={{ marginRight: 8 }} /> : null}
            Save engagement
          </button>
        </div>
      </div>
    </div>
  );
};
